import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import type { LTEscalationRecord } from './types';

interface EscalationListResponse {
  escalations: LTEscalationRecord[];
  total: number;
  /** Grouped reads: the page's groups, in order; `total` counts groups. */
  groups?: { key: string | null; ids: string[] }[];
}

export interface FacetRange { facet: string; op: '<' | '<=' | '>' | '>=' | '='; value: number }
export interface FacetOrder { field: string; direction?: 'asc' | 'desc'; numeric?: boolean }

/** The faceted-query elements shared by the list/available hooks. */
export interface FacetFilters {
  roles?: string[];
  facets?: Record<string, unknown>;
  block?: Record<string, unknown>[];
  range?: FacetRange[];
  exists?: string[];
  available?: boolean;
  /** Only rows past their role's priority threshold — the Pace Board pill's
   *  exact predicate, so a jeopardy list's total equals the pill's count. */
  jeopardy?: boolean;
  orderBy?: FacetOrder[];
  /**
   * List rows arrive without `envelope`/`escalation_payload` by default —
   * pass 'envelope' only when the surface's schema interpolates them.
   */
  include?: 'envelope';
  /** Page by groups of this metadata key (whole groups per page). */
  groupBy?: string;
}

export interface EscalationFilters extends FacetFilters {
  status?: string;
  role?: string;
  type?: string;
  subtype?: string;
  assigned_to?: string;
  parent_id?: string;
  claimed?: boolean;
  priority?: number;
  limit?: number;
  offset?: number;
  sort_by?: string;
  order?: string;
  search?: string;
  enabled?: boolean;
  staleTime?: number;
}

/** JSON-encode the faceted-query elements onto the URL (the route JSON-parses them). */
function appendFacetParams(params: URLSearchParams, f: FacetFilters): void {
  if (f.facets && Object.keys(f.facets).length) params.set('facets', JSON.stringify(f.facets));
  if (f.block?.length) params.set('block', JSON.stringify(f.block));
  if (f.range?.length) params.set('range', JSON.stringify(f.range));
  if (f.exists?.length) params.set('exists', JSON.stringify(f.exists));
  if (f.roles?.length) params.set('roles', JSON.stringify(f.roles));
  if (f.orderBy?.length) params.set('orderBy', JSON.stringify(f.orderBy));
  if (f.available !== undefined) params.set('available', String(f.available));
  if (f.jeopardy === true) params.set('jeopardy', '1');
  if (f.include) params.set('include', f.include);
  if (f.groupBy) params.set('groupBy', f.groupBy);
}

export interface EscalationStats {
  pending: number;
  claimed: number;
  created: number;
  resolved: number;
  by_role: { role: string; pending: number; claimed: number }[];
  by_type: { type: string; pending: number; claimed: number; resolved: number }[];
}

export interface StationMetricPeriod {
  p99: number | null;
  p50: number | null;
  avg: number | null;
  max: number | null;
}

export interface StationMetric {
  role: string;
  pending: number;
  claimed: number;
  resolved: number;
  /**
   * Pending, unclaimed items past the role's threshold — the Pace Board
   * rebalance signal. Age is measured from the role's priority_facet metadata
   * timestamp (created_at when unset) against priority_threshold_minutes
   * (sla_minutes when unset).
   */
  priority_count: number;
  throughput_pct: number | null;
  wait: StationMetricPeriod;
  work: StationMetricPeriod;
}

export function useStationMetrics(
  period?: string,
  facets?: Record<string, unknown>,
  opts?: { enabled?: boolean },
) {
  const p = period ?? '24h';
  // A device-bound facet scope (link variables) narrows every station count.
  const facetJson = facets && Object.keys(facets).length ? JSON.stringify(facets) : null;
  return useQuery<{ stations: StationMetric[] }>({
    queryKey: ['stationMetrics', p, facetJson],
    queryFn: () => apiFetch(
      `/escalations/station-metrics?period=${p}${facetJson ? `&facets=${encodeURIComponent(facetJson)}` : ''}`,
    ),
    staleTime: 5_000,
    enabled: opts?.enabled ?? true,
  });
}

export function useEscalationStats(period?: string) {
  const params = period ? `?period=${period}` : '';
  return useQuery<EscalationStats>({
    queryKey: ['escalationStats', period],
    queryFn: () => apiFetch(`/escalations/stats${params}`),
  });
}

export function useEscalationTypes() {
  return useQuery<{ types: string[] }>({
    queryKey: ['escalationTypes'],
    queryFn: () => apiFetch('/escalations/types'),
  });
}

/** Distinct top-level metadata facet keys the caller may query (role-scoped). */
export function useFacetKeys(enabled = true) {
  return useQuery<{ keys: string[] }>({
    queryKey: ['escalationFacetKeys'],
    queryFn: () => apiFetch('/escalations/facet-keys'),
    enabled,
    staleTime: 60_000,
  });
}

/** Distinct VALUES for one facet key (role-scoped) — the value picker's choices. */
export function useFacetValues(key: string | null | undefined) {
  return useQuery<{ values: string[] }>({
    queryKey: ['escalationFacetValues', key],
    queryFn: () => apiFetch(`/escalations/facet-values?key=${encodeURIComponent(key as string)}`),
    enabled: !!key,
    staleTime: 60_000,
  });
}

/** The list endpoint's query string for a set of filters. */
export function escalationListParams(rest: Omit<EscalationFilters, 'enabled' | 'staleTime'>): URLSearchParams {
  const params = new URLSearchParams();
  if (rest.status) params.set('status', rest.status);
  if (rest.role) params.set('role', rest.role);
  if (rest.type) params.set('type', rest.type);
  if (rest.subtype) params.set('subtype', rest.subtype);
  if (rest.assigned_to) params.set('assigned_to', rest.assigned_to);
  if (rest.parent_id) params.set('parent_id', rest.parent_id);
  if (rest.claimed) params.set('claimed', 'true');
  if (rest.priority) params.set('priority', String(rest.priority));
  if (rest.limit) params.set('limit', String(rest.limit));
  if (rest.offset !== undefined) params.set('offset', String(rest.offset));
  if (rest.sort_by) params.set('sort_by', rest.sort_by);
  if (rest.order) params.set('order', rest.order);
  if (rest.search) params.set('search', rest.search);
  appendFacetParams(params, rest);
  return params;
}

/** One list read: the query key and fetch every list surface shares, so reads dedupe and refresh together. */
export function escalationListQuery(rest: Omit<EscalationFilters, 'enabled' | 'staleTime'>) {
  return {
    queryKey: ['escalations', rest] as const,
    queryFn: () => apiFetch<EscalationListResponse>(`/escalations?${escalationListParams(rest)}`),
  };
}

export function useEscalations(filters: EscalationFilters) {
  const { enabled = true, staleTime, ...rest } = filters;
  return useQuery<EscalationListResponse>({
    ...escalationListQuery(rest),
    enabled,
    ...(staleTime !== undefined ? { staleTime } : {}),
  });
}

export function useAvailableEscalations(filters: Omit<EscalationFilters, 'status'>) {
  const { enabled = true, staleTime, ...rest } = filters;
  const params = new URLSearchParams();
  if (rest.role) params.set('role', rest.role);
  if (rest.type) params.set('type', rest.type);
  if (rest.subtype) params.set('subtype', rest.subtype);
  if (rest.priority) params.set('priority', String(rest.priority));
  if (rest.limit) params.set('limit', String(rest.limit));
  if (rest.offset !== undefined) params.set('offset', String(rest.offset));
  if (rest.sort_by) params.set('sort_by', rest.sort_by);
  if (rest.order) params.set('order', rest.order);
  if (rest.search) params.set('search', rest.search);
  appendFacetParams(params, rest);

  return useQuery<EscalationListResponse>({
    queryKey: ['escalations', 'available', rest],
    queryFn: () => apiFetch(`/escalations/available?${params}`),
    enabled,
    ...(staleTime !== undefined ? { staleTime } : {}),
  });
}

export function useEscalationsByWorkflowId(workflowId: string | undefined) {
  return useQuery<{ escalations: LTEscalationRecord[] }>({
    queryKey: ['escalations', 'by-workflow', workflowId],
    queryFn: () => apiFetch(`/escalations/by-workflow/${workflowId}`),
    enabled: !!workflowId,
  });
}

export function useEscalation(id: string) {
  return useQuery<LTEscalationRecord>({
    queryKey: ['escalations', id],
    queryFn: () => apiFetch(`/escalations/${id}`),
    enabled: !!id,
  });
}

export interface EscalationLookup {
  domain: string;
  key: string;
  /** The edition served; null when a current ref found none. */
  version: number | null;
  /** The ref follows the newest edition. */
  current?: true;
  as?: string;
  data: Record<string, unknown> | null;
  missing?: boolean;
}

/**
 * The escalation's knowledge lookups, resolved server-side against the refs
 * on the row. Read again each time the detail page mounts so a ref that
 * follows the newest edition shows it; never on focus or on a timer.
 */
export function useEscalationLookups(id: string, enabled: boolean) {
  return useQuery<{ lookups: EscalationLookup[] }>({
    queryKey: ['escalations', id, 'lookups'],
    queryFn: () => apiFetch(`/escalations/${id}/lookups`),
    enabled: !!id && enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useClaimEscalation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, durationMinutes }: { id: string; durationMinutes: number }) =>
      apiFetch(`/escalations/${id}/claim`, {
        method: 'POST',
        body: JSON.stringify({ durationMinutes }),
      }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ['escalations'] });
      queryClient.resetQueries({ queryKey: ['escalationStats'] });
    },
  });
}

export function useReleaseEscalation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/escalations/${id}/release`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ['escalations'] });
      queryClient.resetQueries({ queryKey: ['escalationStats'] });
    },
  });
}

export function useResolveEscalation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      resolverPayload,
    }: {
      id: string;
      resolverPayload: Record<string, unknown>;
    }) =>
      apiFetch(`/escalations/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolverPayload }),
      }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ['escalations'] });
      queryClient.resetQueries({ queryKey: ['escalationStats'] });
      queryClient.resetQueries({ queryKey: ['tasks'] });
      queryClient.resetQueries({ queryKey: ['jobs'] });
    },
  });
}

/** Outcomes a roster action refuses with before its act runs. */
export const ROSTER_ACTION_OUTCOMES = {
  NEEDS_BADGE: 'needs_badge',
  ROLE_REFUSED: 'role_refused',
  NOT_PRIMED: 'not_primed',
  CODE_MISMATCH: 'code_mismatch',
} as const;

export interface RosterActionRequest {
  /** The host row whose form declares the roster. */
  id: string;
  field: string;
  /** The action's index among the field's actions. */
  action: number;
  /** The entry's key. */
  entry: string;
  /** A key-in's entered code. */
  code?: string;
}

/**
 * Run one roster entry's action on the server: it re-reads the action from
 * the row's form, checks the entry and any role limit, and acts as the
 * person acting (a live badge rides the request as every write does).
 */
export function useRosterAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: RosterActionRequest) =>
      apiFetch<{ outcome: string }>(`/escalations/${id}/roster-action`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'] });
      queryClient.invalidateQueries({ queryKey: ['escalationStats'] });
    },
  });
}

export interface AccumulateItemResponse {
  outcome: 'accepted' | 'completed';
  count: number;
  remaining: number | null;
  escalationId: string;
  signaled?: boolean;
}

/** Add one item to an open accumulator escalation (POST /:id/accumulate). */
export function useAccumulateItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, itemKey, payload }: { id: string; itemKey: string; payload?: Record<string, unknown> }) =>
      apiFetch<AccumulateItemResponse>(`/escalations/${id}/accumulate`, {
        method: 'POST',
        body: JSON.stringify({ itemKey, ...(payload ? { payload } : {}) }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'] });
    },
  });
}

/** Remove one held item from an open accumulator escalation (POST /:id/remove-item). */
export function useRemoveAccumulatedItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, itemKey }: { id: string; itemKey: string }) =>
      apiFetch<{ outcome: 'removed'; count: number; escalationId: string }>(`/escalations/${id}/remove-item`, {
        method: 'POST',
        body: JSON.stringify({ itemKey }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'] });
    },
  });
}

export function useEscalateToRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetRole }: { id: string; targetRole: string }) =>
      apiFetch(`/escalations/${id}/escalate`, {
        method: 'PATCH',
        body: JSON.stringify({ targetRole }),
      }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ['escalations'] });
      queryClient.resetQueries({ queryKey: ['escalationStats'] });
    },
  });
}

export function useSetEscalationPriority() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, priority }: { ids: string[]; priority: 1 | 2 | 3 | 4 }) =>
      apiFetch<{ updated: number }>('/escalations/priority', {
        method: 'PATCH',
        body: JSON.stringify({ ids, priority }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['escalationStats'], refetchType: 'all' });
    },
  });
}

export function useBulkClaimEscalations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, durationMinutes }: { ids: string[]; durationMinutes: number }) =>
      apiFetch<{ claimed: number; skipped: number }>('/escalations/bulk-claim', {
        method: 'POST',
        body: JSON.stringify({ ids, durationMinutes }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['escalationStats'], refetchType: 'all' });
    },
  });
}

export function useBulkAssignEscalations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      targetUserId,
      durationMinutes,
      reassign,
    }: {
      ids: string[];
      targetUserId: string;
      durationMinutes: number;
      /** Also take over rows under a live claim (admin/superadmin). */
      reassign?: boolean;
    }) =>
      apiFetch<{ assigned: number; skipped: number }>('/escalations/bulk-assign', {
        method: 'POST',
        body: JSON.stringify({ ids, targetUserId, durationMinutes, ...(reassign ? { reassign: true } : {}) }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['escalationStats'], refetchType: 'all' });
    },
  });
}

export function useBulkUnassignEscalations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids }: { ids: string[] }) =>
      apiFetch<{ unassigned: number; skipped: number }>('/escalations/bulk-unassign', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['escalationStats'], refetchType: 'all' });
    },
  });
}

export function useBulkEscalateToRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, targetRole }: { ids: string[]; targetRole: string }) =>
      apiFetch<{ updated: number }>('/escalations/bulk-escalate', {
        method: 'PATCH',
        body: JSON.stringify({ ids, targetRole }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['escalationStats'], refetchType: 'all' });
    },
  });
}

export function useBulkTriageEscalations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, hint }: { ids: string[]; hint?: string }) =>
      apiFetch<{ triaged: number; workflows: string[] }>('/escalations/bulk-triage', {
        method: 'POST',
        body: JSON.stringify({ ids, ...(hint ? { hint } : {}) }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['escalationStats'], refetchType: 'all' });
    },
  });
}

export function useCancelEscalation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ cancelled: boolean; escalationId: string }>(`/escalations/${id}/cancel`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ['escalations'] });
      queryClient.resetQueries({ queryKey: ['escalationStats'] });
    },
  });
}

export function useBulkCancelEscalations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids }: { ids: string[] }) =>
      apiFetch<{ cancelled: number; skipped: number }>('/escalations/bulk-cancel', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['escalationStats'], refetchType: 'all' });
    },
  });
}
